import React from 'react';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import shomriLogo from '@/assets/shomri-logo.svg';
import { Gravity, MatterBody } from '@/components/ui/gravity';
import { Typewriter } from '@/components/ui/typewriter';
import { WordRotate } from '@/components/ui/word-rotate';
import { cn } from '@/lib/utils';

const greetings = [
  { text: 'Welcome', color: 'bg-blue-600' },
  { text: 'Bonjour', color: 'bg-amber-400 text-gray-900' },
  { text: 'שלום', color: 'bg-orange-500' },
  { text: 'Hola', color: 'bg-pink-300 text-gray-900' },
  { text: 'Ciao', color: 'bg-teal-700' },
  { text: 'Привет', color: 'bg-rose-400' },
  { text: '你好', color: 'bg-indigo-500' },
  { text: 'مرحبا', color: 'bg-emerald-500' },
  { text: 'Olá', color: 'bg-violet-500' },
  { text: 'Hallo', color: 'bg-cyan-500' },
  { text: 'こんにちは', color: 'bg-yellow-500 text-gray-900' },
  { text: 'Γειά σου', color: 'bg-fuchsia-500' },
];

const SuccessPage = () => {
  const navigate = useNavigate();

  const handleContinue = () => {
    navigate('/confirmation');
  };

  return (
    <div className="relative min-h-screen flex flex-col overflow-hidden bg-white">
      {/* Fixed content overlay - centered, behind gravity */}
      <div className="absolute inset-0 flex flex-col items-center justify-center px-6 pointer-events-none z-20">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-lg mx-auto"
        >
          {/* Main heading - hold to see Hebrew */}
          <div className="pointer-events-auto mb-6">
            <WordRotate
              words={["Welcome to Shomri", "ברוכים הבאים לשומרי"]}
              className="text-5xl sm:text-7xl font-black text-shomri-navy tracking-tight"
              holdToRotate={true}
            />
          </div>

          {/* Subtitle with typewriter effect */}
          <div className="text-gray-600 text-lg sm:text-xl mb-12 min-h-[3rem]">
            <Typewriter
              messages={[
                "You've officially joined Shomri — we're so excited to have you! Let's go over a few things.",
                "Hey, you're still here? Did you try clicking on 'Welcome to Shomri'? Give it a try!",
                "Okay, let's get a move on — press the button already!",
                "Okay, we're not kidding. Bye! 👋",
                "Wow. Commitment. We respect that.",
                "At this point we're basically hanging out.",
                "Fun fact: staring at this screen does not press the button.",
                "The button isn't shy. You can click it.",
                "Is this a staring contest? Because the page is winning.",
                "Somewhere, a developer is whispering 'please just click it.'",
                "We tested this. Most people click by now.",
                "If you're waiting for something to happen… this is it.",
                "Legend says if you wait long enough, nothing still happens.",
                "Okay this is getting awkward. We'll look away. Click the button."
              ]}
              typeSpeed={35}
              deleteSpeed={15}
              delayBetweenMessages={10000}
              cursor={true}
            />
          </div>

          {/* Installation Complete */}
          <div className="flex items-center justify-center gap-3 text-emerald-600 mb-6">
            <CheckCircle className="w-7 h-7" />
            <span className="text-lg sm:text-xl font-medium">Installation Complete</span>
          </div>

          {/* CTA Button */}
          <div className="mb-16">
            <Button
              onClick={handleContinue}
              className={cn(
                "pointer-events-auto relative overflow-hidden rounded-full px-8 py-6 text-lg font-medium",
                "bg-white text-foreground shadow-2xl",
                "transition-all duration-300 hover:scale-105"
              )}
            >
              {/* Animated gradient border */}
              <motion.span
                className="absolute inset-0 rounded-full"
                style={{
                  background: "linear-gradient(90deg, #8f75f6, #61c8f8, #17c483, #fdc557, #fd66a0, #8f75f6)",
                  backgroundSize: "300% 100%",
                }}
                animate={{
                  backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "linear",
                }}
              />
              {/* Inner background */}
              <span className="absolute inset-[2px] rounded-full bg-white" />
              {/* Content */}
              <span className="relative z-10 flex items-center gap-2 text-gray-900">
                What to Expect
                <ArrowRight className="w-5 h-5" />
              </span>
            </Button>
          </div>

          {/* Logo */}
          <div>
            <img 
              src={shomriLogo} 
              alt="Shomri" 
              className="h-8 mx-auto opacity-60" 
            />
          </div>
        </motion.div>
      </div>

      {/* Gravity area with falling greetings */}
      <div className="absolute inset-0 z-10">
        <Gravity 
          gravity={{ x: 0, y: 1 }} 
          grabCursor={true} 
          addTopWall={false}
          className="w-full h-full"
        >
          {greetings.map((greeting, index) => (
            <MatterBody
              key={greeting.text}
              className="pointer-events-none"
              matterBodyOptions={{ 
                friction: 0.3, 
                restitution: 0.4,
                density: 0.001
              }}
              x={`${5 + (index % 6) * 16}%`}
              y={-60 - (index * 40)}
              angle={Math.random() * 30 - 15}
            >
              <span className={`${greeting.color} text-white px-20 py-10 rounded-full text-5xl sm:text-6xl font-bold shadow-2xl whitespace-nowrap select-none`}>
                {greeting.text}
              </span>
            </MatterBody>
          ))}
        </Gravity>
      </div>
    </div>
  );
};

export default SuccessPage;
